import React, { useEffect, useMemo, useRef } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import type { EventImage } from '@/pages/Dashboard/AddEditEvent'
import { Label } from '../ui/label'
import type { EventUser } from '@/pages/Dashboard/AddEditEvent'
import {
  createMarkup,
  isContentEmpty,
  locationOptions,
  registrationOptions,
} from '@/constants/event'
import './EventPreviewDrawer.css'
import {
  capitalizeString,
  type CategoryData,
  type TagData,
} from '@/utils/helper'
import { formatDateDisplay, formatTime12HourString } from '@/utils/dateUtils'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import { Badge } from '../ui/badge'
import { formatPriceWithCommas } from '@/utils/formFieldUtils'
import { PdfIcon } from '@/components/Icons'

type OrganizerFields =
  | 'organizer'
  | 'organizerEmail'
  | 'organizerPhone'
  | 'organizerWebsite'

interface EventPreviewDrawerProps {
  readonly trigger: React.ReactNode
  readonly event?: Omit<
    EventFormDataType,
    OrganizerFields | 'eventHighlights'
  > &
    Partial<Pick<EventFormDataType, OrganizerFields | 'eventHighlights'>>
  categoryData?: CategoryData[]
  tagData?: TagData[]
  isSubmitting?: boolean
  handleSubmit?: (e: React.FormEvent<HTMLButtonElement>) => Promise<void>
  isSSAdmin?: boolean
}

export default function EventPreviewDrawer({
  trigger,
  event = {
    eventName: '',
    description: '',
    category: '',
    tag: [],
    eventImages: [],
    eventPDF: undefined,
    timeZone: '',
    location: 'online',
    zoomLink: '',
    venueName: '',
    venueAddress: '',
    venueCity: '',
    venueState: '',
    registrationType: 'free',
    eventTicket: [],
    organizer: null,
    organizerEmail: '',
    organizerPhone: '',
    organizerWebsite: '',
    specialInstructions: '',
    users: [],
    venueLatitude: undefined,
    venueLongitude: undefined,
    venueCountry: '',
    startDate: undefined,
    endDate: undefined,
    startTime: undefined,
    endTime: undefined,
    registrationDeadline: undefined,
    sponsorshipPackages: [],
    eventHighlights: [],
  },
  categoryData,
  isSubmitting,
  tagData,
  handleSubmit,
  isSSAdmin = false,
}: Readonly<EventPreviewDrawerProps>) {
  const firstFocusableRef = useRef<HTMLButtonElement | null>(null)
  const drawerContentRef = useRef<HTMLDivElement | null>(null)

  const selectedCategory = useMemo(() => {
    const category = categoryData?.find(
      category => category.id === event.category
    )

    return category?.name ?? event.category
  }, [event.category, categoryData])

  const selectedTags = useMemo(() => {
    let ids: string[] = []
    if (Array.isArray(event.tag)) {
      ids = event.tag.map(t => t.id)
    } else if (event.tag) {
      ids = [String(event.tag)]
    }

    const names = ids
      .map(id => tagData?.find(t => t.id === id)?.name ?? id)
      .filter(Boolean)

    return names
  }, [event.tag, tagData])

  const eventImageUrls = useMemo(() => {
    if (!event.eventImages || event.eventImages.length === 0) {
      return []
    }

    return event.eventImages.map((img: EventImage) => {
      if (img.imageFile instanceof File) {
        return URL.createObjectURL(img.imageFile)
      }
      return img.url
    })
  }, [event.eventImages])

  // Focus management: move focus to close button when drawer opens
  useEffect(() => {
    const el = drawerContentRef.current
    if (el) {
      const first = el.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (first) first.focus()
    }
  }, [])

  // Keyboard: allow Escape to close drawer
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      const closeBtn = drawerContentRef.current?.querySelector<HTMLElement>(
        'button[aria-label^="Close"], button[data-drawer-close]'
      )
      if (closeBtn) closeBtn.click()
    }
  }

  const eventHighlightData = event.eventHighlights
    ? event.eventHighlights?.filter(
        highlight => highlight.title || highlight.shortDescription
      )
    : undefined

  const hasAnyHighlight = eventHighlightData && eventHighlightData.length > 0

  return (
    <Drawer direction="right">
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent
        className="h-full p-0 w-full max-w-max lg:max-w-[990px] rounded-none lg:rounded-xl"
        aria-modal="true"
        aria-label="Event preview drawer"
        onKeyDown={onKeyDown}
      >
        <div
          ref={drawerContentRef}
          className="ml-auto min-h-screen lg:min-h-auto h-full w-full bg-ss-neutral-white overflow-hidden rounded-2xl"
          role="dialog"
          aria-labelledby="event-preview-title"
        >
          {/* Header */}
          <header className="flex items-center justify-between border-b border-black/6 px-4 py-3">
            <DrawerTitle
              id="event-preview-title"
              className="text-lg font-semibold text-ss-neutral-950 leading-7"
            >
              Event Preview
            </DrawerTitle>
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-5 p-0 hover:bg-transparent"
                aria-label="Close event preview drawer"
                data-drawer-close
                ref={firstFocusableRef}
              >
                <X className="h-5 w-5 text-ss-neutral-950" aria-hidden="true" />
              </Button>
            </DrawerClose>
          </header>

          {/* Content */}
          <main
            tabIndex={-1}
            className="flex-1 overflow-y-auto p-4 h-[calc(100vh-120px)] lg:h-[calc(100vh-150px)]"
            aria-labelledby="event-preview-title"
          >
            <div className="space-y-4">
              {/* Basic Information Section */}
              <section
                className="space-y-3"
                aria-labelledby="basic-info-heading"
              >
                <h3
                  id="basic-info-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Basic Information
                </h3>

                {/* Event Name and Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-10">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Event Name
                    </Label>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.eventName}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Event Category
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {selectedCategory}
                    </div>
                  </div>
                </div>

                {selectedTags.length > 0 && (
                  <div>
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Event Tag
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {selectedTags.join(', ')}
                    </div>
                  </div>
                )}

                {/* Event Description */}
                <div className="space-y-1.5">
                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                    Event Description
                  </span>
                  {!isContentEmpty(event.description) && (
                    <div
                      className="prose prose-sm max-w-none [&>p]:m-0 [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:m-0 [&>ul]:mb-2 [&>ul:last-child]:mb-0 [&>ol]:m-0 [&>ol]:mb-2 [&>ol:last-child]:mb-0 [&>li]:mb-1"
                      dangerouslySetInnerHTML={createMarkup(event.description)}
                    />
                  )}
                </div>

                {/* Event Images */}
                <div className="space-y-2">
                  <span
                    className="text-sm font-normal text-ss-neutral-600 leading-4.5 "
                    id="event-images-label"
                  >
                    Event Image
                  </span>
                  <ul
                    className="flex gap-1.5 flex-wrap"
                    aria-labelledby="event-images-label"
                  >
                    {eventImageUrls.length > 0 ? (
                      <ul
                        className="flex gap-1.5 flex-wrap"
                        aria-labelledby="event-images-label"
                      >
                        {eventImageUrls.map(
                          (imageUrl: string | undefined, index: number) => (
                            <li
                              key={`image-${index}`}
                              className="w-[60px] h-[60px] rounded bg-ss-neutral-200 overflow-hidden"
                            >
                              <img
                                src={imageUrl}
                                alt={`${event.eventName} ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={e => {
                                  const target = e.target as HTMLImageElement
                                  target.style.backgroundColor = '#f3f4f6'
                                  target.style.display = 'flex'
                                  target.style.alignItems = 'center'
                                  target.style.justifyContent = 'center'
                                  target.style.fontSize = '10px'
                                  target.style.color = '#6b7280'
                                  target.alt = 'Failed to load'
                                }}
                                loading="lazy"
                              />
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      <div className="text-sm text-ss-neutral-500 italic">
                        No images selected
                      </div>
                    )}
                  </ul>
                </div>

                {/* Event PDF */}
                {event.eventPDF && event.eventPDF.pdfFile && (
                  <div className="space-y-2">
                    <span
                      className="text-sm font-normal text-ss-neutral-600 leading-4.5"
                      id="BPF-schedule-of-event-pdf-label"
                    >
                      BPF Schedule of Events PDF
                    </span>
                    <div
                      className="flex items-center gap-2 p-2 bg-ss-neutral-25 rounded-lg w-fit"
                      aria-labelledby="BPF-schedule-of-event-pdf-label"
                    >
                      <PdfIcon className="h-8 w-8 text-ss-critical-200 flex-shrink-0" />
                      <span className="text-sm font-medium text-ss-neutral-950 break-all">
                        {event.eventPDF.pdfFile.name}
                      </span>
                    </div>
                  </div>
                )}

                {event.eventInfoPDF && event.eventInfoPDF.pdfFile && (
                  <div className="space-y-2">
                    <span
                      className="text-sm font-normal text-ss-neutral-600 leading-4.5"
                      id="about-park-city-pdf-label"
                    >
                      About Park City PDF
                    </span>
                    <div
                      className="flex items-center gap-2 p-2 bg-ss-neutral-25 rounded-lg w-fit"
                      aria-labelledby="about-park-city-pdf-label"
                    >
                      <PdfIcon className="h-8 w-8 text-ss-critical-200 flex-shrink-0" />
                      <span className="text-sm font-medium text-ss-neutral-950 break-all">
                        {event.eventInfoPDF.pdfFile.name}
                      </span>
                    </div>
                  </div>
                )}
              </section>

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              {/* Date & Time Section */}
              <section
                className="space-y-3"
                aria-labelledby="date-time-heading"
              >
                <h3
                  id="date-time-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Date & Time
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Start Date
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.startDate
                        ? formatDateDisplay(event.startDate)
                        : ''}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      End Date
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.endDate ? formatDateDisplay(event.endDate) : ''}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Start Time
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.startTime
                        ? formatTime12HourString(event.startTime)
                        : ''}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      End Time
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.endTime
                        ? formatTime12HourString(event.endTime)
                        : ''}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Time Zone
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.timeZone}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Registration Deadline
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {event.registrationDeadline
                        ? formatDateDisplay(event.registrationDeadline)
                        : ''}
                    </div>
                  </div>
                </div>
              </section>

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              {/* Location Section */}
              <section className="space-y-3" aria-labelledby="location-heading">
                <h3
                  id="location-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Location
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-8">
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Event Format
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {capitalizeString(event.location)}
                    </div>
                  </div>
                  {event.location !== locationOptions[1].id && (
                    <>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Meeting Link
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.zoomLink}
                        </div>
                      </div>
                    </>
                  )}
                  {event.location !== locationOptions[0].id && (
                    <>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Venue Name
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.venueName}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Address
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.venueAddress}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          City
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.venueCity}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          State
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.venueState}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </section>

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              {/* Pricing & Registration Section */}
              <section className="space-y-3" aria-labelledby="pricing-heading">
                <h3
                  id="pricing-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Pricing & Registration
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-8">
                  <div className="space-y-1.5">
                    <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                      Registration Type
                    </span>
                    <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                      {capitalizeString(event.registrationType)}
                    </div>
                  </div>
                </div>
                {event.registrationType === registrationOptions[0].id && (
                  <section
                    className="space-y-3"
                    aria-labelledby={`ticket-free-heading`}
                  >
                    <div className="bg-ss-neutral-25 rounded px-3 py-1.5 w-full">
                      <h5
                        id={`ticket-free-heading`}
                        className="text-base font-bold text-ss-neutral-950 leading-[22px]"
                      >
                        Ticket
                      </h5>
                    </div>

                    {/* Responsive Grid Container */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-8">
                      {/* Ticket Name */}
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Ticket Name
                        </span>
                        <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                          {event?.ticketName}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Description
                        </span>
                        <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                          {event?.ticketDescription}
                        </div>
                      </div>

                      {/* What's Included */}
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          What's Included
                        </span>
                        {!isContentEmpty(event?.ticketPerks ?? '') && (
                          <div
                            className="prose prose-sm max-w-none text-base font-medium text-ss-neutral-950 leading-[22px] break-words [&>p]:m-0 [&>p]:mb-1 [&>ul]:m-0 [&>ul]:mb-1 [&>ol]:m-0 [&>ol]:mb-1"
                            dangerouslySetInnerHTML={createMarkup(
                              event?.ticketPerks ?? ''
                            )}
                          />
                        )}
                      </div>

                      {/* Max Attendees */}
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Max Attendees Limit
                        </span>
                        <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                          {event?.maxAttendees}
                        </div>
                      </div>
                    </div>
                  </section>
                )}
                {event.registrationType === registrationOptions[1].id &&
                  event.eventTicket && (
                    <>
                      {/* Ticket Information Section */}
                      <div
                        className="space-y-4 w-full"
                        aria-labelledby="tickets-heading"
                      >
                        <div className="space-y-4">
                          {event.eventTicket.map((ticket, index) => (
                            <section
                              key={ticket.id || `ticket-${index}`}
                              className="space-y-3"
                              aria-labelledby={`ticket-${index + 1}-heading`}
                            >
                              <div className="bg-ss-neutral-25 rounded px-3 py-1.5 w-full">
                                <h5
                                  id={`ticket-${index + 1}-heading`}
                                  className="text-base font-bold text-ss-neutral-950 leading-[22px]"
                                >
                                  Ticket {index + 1}
                                </h5>
                              </div>

                              {/* Responsive Grid Container */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-8">
                                {/* Ticket Type */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    Ticket Type
                                  </span>
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    {ticket.isSubEventTicket
                                      ? 'Sub Event'
                                      : 'Main Event'}
                                  </div>
                                </div>

                                {/* Ticket Name */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    Ticket Name
                                  </span>
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    {ticket.name}
                                  </div>
                                </div>

                                {/* Ticket Price */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    Ticket Price
                                  </span>
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    ${formatPriceWithCommas(ticket.price || '')}
                                  </div>
                                </div>

                                {/* Description */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    Description
                                  </span>
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    {ticket.description}
                                  </div>
                                </div>

                                {/* What's Included */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    What's Included
                                  </span>
                                  {!isContentEmpty(ticket.perks ?? '') && (
                                    <div
                                      className="prose prose-sm max-w-none text-base font-medium text-ss-neutral-950 leading-[22px] break-words [&>p]:m-0 [&>p]:mb-1 [&>ul]:m-0 [&>ul]:mb-1 [&>ol]:m-0 [&>ol]:mb-1"
                                      dangerouslySetInnerHTML={createMarkup(
                                        ticket.perks ?? ''
                                      )}
                                    />
                                  )}
                                </div>

                                {/* Max Attendees */}
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                                    Max Attendees for this Ticket
                                  </span>
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    {ticket?.maxAttendeesLimit}
                                  </div>
                                </div>
                              </div>
                            </section>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
              </section>

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              <section className="space-y-4" aria-labelledby="packages-heading">
                <h3
                  id="packages-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Sponsor Packages
                </h3>
                <div className="space-y-4">
                  {event?.sponsorshipPackages &&
                  event.sponsorshipPackages.length > 0 ? (
                    event.sponsorshipPackages
                      ?.filter(pkg => pkg.name || pkg.description || pkg.price)
                      .map((pkg, index) => (
                        <section
                          key={pkg.id || `package-${index}`}
                          className="space-y-3"
                          aria-labelledby={`package-${index + 1}-heading`}
                        >
                          <div className="bg-ss-neutral-25 rounded px-3 py-1.5 w-full flex items-center justify-between">
                            <h5
                              id={`package-${index + 1}-heading`}
                              className="text-base font-bold text-ss-neutral-950 leading-[22px]"
                            >
                              {`Package ${index + 1}`}
                            </h5>
                            {pkg.priority && (
                              <Badge className="bg-ss-blue-50 text-ss-blue-900 border-ss-blue-200 pointer-events-none">
                                Priority {pkg.priority}
                              </Badge>
                            )}
                          </div>

                          {/* Responsive Grid Container */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-8">
                            {pkg.name && (
                              <div className="space-y-1.5">
                                <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                  Package Name
                                </span>
                                <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                  {pkg.name}
                                </div>
                              </div>
                            )}
                            {/* Package Price */}
                            {pkg.price && (
                              <div className="space-y-1.5">
                                <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                  Package Price
                                </span>
                                <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                  ${formatPriceWithCommas(pkg.price)}
                                </div>
                              </div>
                            )}

                            {/* Description */}
                            {pkg.description && (
                              <div className="space-y-1.5">
                                <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                  Description
                                </span>
                                {!isContentEmpty(pkg.description) ? (
                                  <div
                                    className="prose prose-sm max-w-none text-base font-medium text-ss-neutral-950 leading-[22px] break-words [&>p]:m-0 [&>p]:mb-1 [&>ul]:m-0 [&>ul]:mb-1 [&>ol]:m-0 [&>ol]:mb-1"
                                    dangerouslySetInnerHTML={createMarkup(
                                      pkg.description
                                    )}
                                  />
                                ) : (
                                  <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                    {pkg.description}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Max Sponsorships Limit */}
                            {pkg.maxSponsorshipsLimit && (
                              <div className="space-y-1.5">
                                <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                  Max Sponsorships Limit
                                </span>
                                <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                  {pkg.maxSponsorshipsLimit}
                                </div>
                              </div>
                            )}

                            {/* Hero Flight Duration */}
                            {pkg.heroDuration && (
                              <div className="space-y-1.5">
                                <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                  Hero Flight Duration
                                </span>
                                <div className="text-base font-medium text-ss-neutral-950 leading-[22px] break-words">
                                  {pkg.heroDuration} days
                                </div>
                              </div>
                            )}

                            {/* Available Ad Slots */}
                            {pkg.availablePlacements &&
                              pkg.availablePlacements.length > 0 && (
                                <div className="space-y-1.5">
                                  <span className="text-sm font-normal text-ss-neutral-600 leading-[18px]">
                                    Available Ad Slots
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {pkg.availablePlacements.map(placement => (
                                      <Badge
                                        key={placement}
                                        className="bg-ss-neutral-100 text-ss-neutral-950 border-ss-neutral-300 pointer-events-none"
                                      >
                                        {capitalizeString(
                                          placement.split('_').join(' ')
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </section>
                      ))
                  ) : (
                    <div className="text-sm text-ss-neutral-400 italic text-center py-4">
                      No sponsorship packages configured
                    </div>
                  )}
                </div>
              </section>
              {isSSAdmin && (
                <>
                  {/* Separator */}
                  <div className="h-px bg-ss-neutral-50 w-full" />

                  {/* Event Highlights Section */}
                  <section
                    className="space-y-4"
                    aria-labelledby="highlights-heading"
                  >
                    <h3
                      id="highlights-heading"
                      className="text-lg font-semibold text-ss-neutral-950 leading-7"
                    >
                      Event Highlights
                    </h3>
                    <div className="space-y-4">
                      {hasAnyHighlight &&
                        eventHighlightData.map((highlight, index) => (
                          <section
                            key={highlight.id || `highlight-${index}`}
                            className="space-y-3 bg-ss-neutral-25 rounded-lg p-4"
                            aria-labelledby={`highlight-${index + 1}-heading`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-3">
                                {/* Title */}
                                {highlight.title && (
                                  <h5
                                    id={`highlight-${index + 1}-heading`}
                                    className="text-base font-bold text-ss-neutral-950 leading-[22px]"
                                  >
                                    {highlight.title}
                                  </h5>
                                )}

                                {/* Description */}
                                {highlight.shortDescription && (
                                  <div className="space-y-1.5">
                                    {!isContentEmpty(
                                      highlight.shortDescription
                                    ) ? (
                                      <div
                                        className="prose prose-sm max-w-none text-sm font-normal text-ss-neutral-600 leading-[18px] break-words [&>p]:m-0 [&>p]:mb-1 [&>ul]:m-0 [&>ul]:mb-1 [&>ol]:m-0 [&>ol]:mb-1"
                                        dangerouslySetInnerHTML={createMarkup(
                                          highlight.shortDescription
                                        )}
                                      />
                                    ) : (
                                      <div className="text-sm font-normal text-ss-neutral-600 leading-[18px] break-words">
                                        {highlight.shortDescription}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Date Range */}
                                {(highlight.startDate || highlight.endDate) && (
                                  <div className="flex flex-wrap items-center gap-2 text-sm text-ss-neutral-600">
                                    {highlight.startDate && (
                                      <div className="flex items-center gap-1">
                                        <span className="font-medium">
                                          From:
                                        </span>
                                        <span>
                                          {formatDateDisplay(
                                            highlight.startDate
                                          )}
                                          {highlight.startDate instanceof
                                            Date &&
                                            ` at ${formatTime12HourString(highlight.startDate)}`}
                                        </span>
                                      </div>
                                    )}
                                    {highlight.endDate && (
                                      <div className="flex items-center gap-1">
                                        <span className="font-medium">To:</span>
                                        <span>
                                          {formatDateDisplay(highlight.endDate)}
                                          {highlight.endDate instanceof Date &&
                                            ` at ${formatTime12HourString(highlight.endDate)}`}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Image */}
                              {(highlight.eventImage ||
                                highlight.imageFile) && (
                                <div className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-ss-neutral-100">
                                  <img
                                    src={
                                      highlight.imageFile
                                        ? URL.createObjectURL(
                                            highlight.imageFile
                                          )
                                        : highlight.eventImage || ''
                                    }
                                    alt={highlight.title || 'Event highlight'}
                                    className="w-full h-full object-cover"
                                    onError={e => {
                                      const target =
                                        e.target as HTMLImageElement
                                      target.style.display = 'none'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </section>
                        ))}
                      {!hasAnyHighlight && (
                        <div className="text-sm text-ss-neutral-400 italic text-center py-4">
                          No event highlights configured
                        </div>
                      )}
                    </div>
                  </section>
                </>
              )}

              {isSSAdmin && (
                <>
                  {/* Separator */}
                  <div className="h-px bg-ss-neutral-50 w-full" />

                  {/* Organizer Information Section */}
                  <section
                    className="space-y-3"
                    aria-labelledby="organizer-heading"
                  >
                    <h3
                      id="organizer-heading"
                      className="text-lg font-semibold text-ss-neutral-950 leading-7"
                    >
                      Organizer Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Organizer Name
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {`${event.organizer?.firstName ?? ''} ${event.organizer?.lastName ?? ''}`.trim()}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5  ">
                          Organizer Email
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.organizerEmail}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          Organizer Phone
                        </span>
                        <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                          {event.organizerPhone}
                        </div>
                      </div>
                      {event.organizerWebsite && (
                        <div className="space-y-1.5">
                          <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                            Organizer Website
                          </span>
                          <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                            {event.organizerWebsite}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                </>
              )}

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              {/* Additional Information Section */}
              <section
                className="space-y-3"
                aria-labelledby="additional-heading"
              >
                <h3
                  id="additional-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Additional Information
                </h3>
                <div className="space-y-1.5">
                  <span className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                    Special Instructions
                  </span>
                  {!isContentEmpty(event.specialInstructions) && (
                    <div
                      className="prose prose-sm max-w-none [&>p]:m-0 [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:m-0 [&>ul]:mb-2 [&>ul:last-child]:mb-0 [&>ol]:m-0 [&>ol]:mb-2 [&>ol:last-child]:mb-0 [&>li]:mb-1"
                      dangerouslySetInnerHTML={createMarkup(
                        event.specialInstructions
                      )}
                    />
                  )}
                </div>
              </section>

              {/* Separator */}
              <div className="h-px bg-ss-neutral-50 w-full" />

              {/* Users Section */}
              <section className="space-y-3" aria-labelledby="users-heading">
                <h3
                  id="users-heading"
                  className="text-lg font-semibold text-ss-neutral-950 leading-7"
                >
                  Users
                </h3>
                <ul
                  className="flex flex-wrap items-center gap-8"
                  aria-labelledby="users-heading"
                >
                  {event.users
                    .filter(user => {
                      return !(
                        !user.fullName &&
                        !user.avatarFile &&
                        !user.designation &&
                        !user.profile
                      )
                    })
                    .slice(0, 5)
                    .map((user: EventUser, index: number) => (
                      <li
                        key={user.id || `user-${index}`}
                        className="space-y-1.5"
                      >
                        <Avatar className="w-[66px] h-[66px] rounded-full border border-ss-neutral-50">
                          {user.avatarFile ? (
                            <AvatarImage
                              src={URL.createObjectURL(user.avatarFile)}
                              alt={user.fullName}
                              className="object-cover rounded-full"
                            />
                          ) : (
                            <AvatarFallback className="bg-ss-neutral-100 text-ss-neutral-950 text-lg font-semibold rounded-full">
                              {user.fullName
                                .split(' ')
                                .map((n: string) => n[0])
                                .join('')}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="space-y-0.5">
                          <div className="text-base font-semibold text-ss-neutral-950 leading-[22px] break-all">
                            {user.fullName}
                          </div>
                          <div className="text-sm font-normal text-ss-neutral-600 leading-4.5  break-all">
                            {user.designation}
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </section>
            </div>
          </main>
          <DrawerFooter className="border-t border-ss-neutral-50 bg-white px-6 py-4 shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)] rounded-bl-none rounded-br-none lg:rounded-bl-xl lg:rounded-br-xl">
            <div className="flex items-center justify-between w-full">
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-4 py-2.5 text-sm font-semibold text-ss-neutral-950 border-ss-neutral-200 hover:bg-ss-action-gray-50"
                  aria-label="Back to edit"
                  disabled={isSubmitting}
                >
                  Back to Edit
                </Button>
              </DrawerClose>
              <Button
                className="h-9 px-4 py-2.5 text-sm font-semibold text-ss-neutral-white bg-ss-blue-900 hover:bg-ss-blue-800 rounded-lg"
                aria-label="Publish event"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Publishing...' : 'Publish Event'}
              </Button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
